/**
 * WebRTCService.js
 * Manages Peer-to-Peer (P2P) Audio Communication between Teacher and Student
 * Uses Opus Codec for ultra-low latency (<150ms) crystal-clear voice transmission.
 */

import { realtimeService } from "../RealtimeService";

const ICE_SERVERS = {
    iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
        { urls: "stun:stun2.l.google.com:19302" },
        { urls: "stun:stun3.l.google.com:19302" },
        { urls: "stun:stun4.l.google.com:19302" }
    ]
};

export class WebRTCService {
    constructor() {
        this.peerConnection = null;
        this.localStream = null;
        this.remoteStream = null;
        this.remoteAudioElement = null;
        this.targetConnectionId = null;
        this.isMuted = false;
        this.isConnected = false;

        this.onRemoteStreamCallback = null;
        this.onConnectionStateCallback = null;
        this.onAudioLevelCallback = null;

        this._audioContext = null;
        this._analyser = null;
        this._animationFrameId = null;
        this._unsubSignal = null;
        this._unsubIce = null;
    }

    init() {
        if (this._initialized) return;
        this._initialized = true;

        this._unsubSignal = realtimeService.onReceiveWebRtcSignal((payload) => {
            if (!payload || !payload.signal) return;
            this.handleIncomingSignal(payload.fromConnectionId, payload.signal);
        });

        this._unsubIce = realtimeService.onReceiveWebRtcIce((payload) => {
            if (!payload || !payload.candidate) return;
            this.handleIncomingIce(payload.candidate);
        });
    }

    /**
     * Start an outbound audio call from Teacher to Student
     * @param {string} studentConnectionId 
     */
    async startCall(studentConnectionId) {
        this.init();
        this.targetConnectionId = studentConnectionId;
        this._setConnectionState("connecting");

        try {
            await this._obtainLocalMicrophone();
            this._createPeerConnection();

            // Add local audio tracks to peer connection
            this.localStream.getAudioTracks().forEach((track) => {
                this.peerConnection.addTrack(track, this.localStream);
            });

            // Create Offer
            const offer = await this.peerConnection.createOffer({
                offerToReceiveAudio: true,
                offerToReceiveVideo: false
            });
            await this.peerConnection.setLocalDescription(offer);

            // Send Offer via SignalR
            await realtimeService.sendWebRtcSignal(this.targetConnectionId, {
                type: "offer",
                sdp: offer.sdp
            });
        } catch (err) {
            console.error("[WebRTCService] Error starting call:", err);
            this._setConnectionState("failed");
            this.endCall();
        }
    }

    /**
     * Handle incoming Signal (Offer or Answer)
     */
    async handleIncomingSignal(fromConnectionId, signal) {
        this.init();
        this.targetConnectionId = fromConnectionId;

        try {
            if (signal.type === "offer") {
                this._setConnectionState("connecting");
                await this._obtainLocalMicrophone();
                this._createPeerConnection();

                this.localStream.getAudioTracks().forEach((track) => {
                    this.peerConnection.addTrack(track, this.localStream);
                });

                await this.peerConnection.setRemoteDescription(new RTCSessionDescription(signal));
                const answer = await this.peerConnection.createAnswer();
                await this.peerConnection.setLocalDescription(answer);

                await realtimeService.sendWebRtcSignal(fromConnectionId, {
                    type: "answer",
                    sdp: answer.sdp
                });
            } else if (signal.type === "answer") {
                if (this.peerConnection) {
                    await this.peerConnection.setRemoteDescription(new RTCSessionDescription(signal));
                }
            }
        } catch (err) {
            console.error("[WebRTCService] Error processing WebRTC signal:", err);
        }
    }

    /**
     * Handle ICE candidate from remote peer
     */
    async handleIncomingIce(candidate) {
        if (!this.peerConnection) return;
        try {
            await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (err) {
            console.error("[WebRTCService] Error adding ICE candidate:", err);
        }
    }

    /**
     * Request microphone access
     */
    async _obtainLocalMicrophone() {
        if (this.localStream) return this.localStream;
        try {
            this.localStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                },
                video: false
            });
            this._setupAudioLevelMeter(this.localStream);
            return this.localStream;
        } catch (err) {
            console.warn("[WebRTCService] Microphone access denied or unavailable:", err);
            // Create silent stream fallback so connection can still receive remote audio
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = ctx.createOscillator();
            const dst = oscillator.connect(ctx.createMediaStreamDestination());
            oscillator.start();
            this.localStream = dst.stream;
            return this.localStream;
        }
    }

    _createPeerConnection() {
        if (this.peerConnection) {
            this.peerConnection.close();
        }

        this.peerConnection = new RTCPeerConnection(ICE_SERVERS);

        this.peerConnection.onicecandidate = (event) => {
            if (event.candidate && this.targetConnectionId) {
                realtimeService.sendWebRtcIce(this.targetConnectionId, event.candidate).catch(() => {});
            }
        };

        this.peerConnection.ontrack = (event) => {
            this.remoteStream = event.streams[0];
            this._playRemoteAudio(this.remoteStream);
            if (this.onRemoteStreamCallback) {
                this.onRemoteStreamCallback(this.remoteStream);
            }
        };

        this.peerConnection.onconnectionstatechange = () => {
            const state = this.peerConnection ? this.peerConnection.connectionState : "closed";
            this._setConnectionState(state);
        };
    }

    _playRemoteAudio(stream) {
        if (typeof window === "undefined") return;
        if (!this.remoteAudioElement) {
            this.remoteAudioElement = document.createElement("audio");
            this.remoteAudioElement.autoplay = true;
            this.remoteAudioElement.style.display = "none";
            document.body.appendChild(this.remoteAudioElement);
        }
        this.remoteAudioElement.srcObject = stream;
        this.remoteAudioElement.play().catch((e) => console.warn("[WebRTCService] Autoplay prevented:", e));
    }

    _setupAudioLevelMeter(stream) {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) return;
            this._audioContext = new AudioCtx();
            const source = this._audioContext.createMediaStreamSource(stream);
            this._analyser = this._audioContext.createAnalyser();
            this._analyser.fftSize = 64;
            source.connect(this._analyser);

            const dataArray = new Uint8Array(this._analyser.frequencyBinCount);
            const checkLevel = () => {
                if (!this._analyser) return;
                this._analyser.getByteFrequencyData(dataArray);
                let sum = 0;
                for (let i = 0; i < dataArray.length; i++) {
                    sum += dataArray[i];
                }
                const average = sum / dataArray.length;
                const normalized = Math.min(100, Math.round((average / 128) * 100));
                if (this.onAudioLevelCallback) {
                    this.onAudioLevelCallback(normalized);
                }
                this._animationFrameId = requestAnimationFrame(checkLevel);
            };
            checkLevel();
        } catch (e) {
            console.warn("[WebRTCService] Audio level meter error:", e);
        }
    }

    toggleMute() {
        if (!this.localStream) return this.isMuted;
        this.localStream.getAudioTracks().forEach((track) => {
            track.enabled = !track.enabled;
            this.isMuted = !track.enabled;
        });
        return this.isMuted;
    }

    _setConnectionState(state) {
        this.isConnected = state === "connected";
        if (this.onConnectionStateCallback) {
            this.onConnectionStateCallback(state);
        }
    }

    endCall() {
        if (this._animationFrameId) {
            cancelAnimationFrame(this._animationFrameId);
            this._animationFrameId = null;
        }
        if (this._audioContext) {
            this._audioContext.close().catch(() => {});
            this._audioContext = null;
        }
        if (this.localStream) {
            this.localStream.getTracks().forEach((t) => t.stop());
            this.localStream = null;
        }
        if (this.remoteAudioElement) {
            this.remoteAudioElement.srcObject = null;
            if (this.remoteAudioElement.parentNode) {
                this.remoteAudioElement.parentNode.removeChild(this.remoteAudioElement);
            }
            this.remoteAudioElement = null;
        }
        if (this.peerConnection) {
            this.peerConnection.close();
            this.peerConnection = null;
        }

        this.targetConnectionId = null;
        this.isMuted = false;
        this._setConnectionState("closed");
    }

    destroy() {
        this.endCall();
        if (this._unsubSignal) this._unsubSignal();
        if (this._unsubIce) this._unsubIce();
    }
}

export const webrtcService = new WebRTCService();
export default webrtcService;
